import type { APIRedirect } from '@/types'

import type { FetchResult } from './types'

type ApiOk<T> = { status: 'ok'; data: T }
type ApiRedirectResult = { status: 'redirect'; redirect: APIRedirect }
type ApiNotFound = { status: 'not-found'; httpStatus: 404 | 410 }
type ApiError = { status: 'error'; message: string; httpStatus?: number }

export type ApiResult<T> = ApiOk<T> | ApiNotFound | ApiError
export type ApiResultWithRedirect<T> = ApiOk<T> | ApiRedirectResult | ApiNotFound | ApiError
export type ApiAcknowledgement = { status: 'ok' } | ApiError

function toApiResult<T>({ originalResponse, data, errors }: FetchResult<T>): ApiResult<T> {
    if (originalResponse.ok && data) {
        return {
            status: 'ok',
            data,
        }
    }

    if (originalResponse.status === 404 || originalResponse.status === 410) {
        return {
            status: 'not-found',
            httpStatus: originalResponse.status,
        }
    }

    return {
        status: 'error',
        message: errors[0] || `HTTP ${originalResponse.status}`,
        httpStatus: originalResponse.status,
    }
}

export async function runApiRequest<T>(url: string, fn: () => Promise<FetchResult<T>>): Promise<ApiResult<T>> {
    try {
        return toApiResult(await fn())
    } catch (error) {
        console.error({ url, error })
        return {
            status: 'error',
            message: error instanceof Error ? error.message : String(error),
        }
    }
}

export async function runApiAcknowledgement(
    url: string,
    fn: () => Promise<FetchResult<unknown>>,
): Promise<ApiAcknowledgement> {
    try {
        const { originalResponse, errors } = await fn()
        if (!originalResponse.ok) {
            return {
                status: 'error',
                message: errors[0] || `HTTP ${originalResponse.status}`,
                httpStatus: originalResponse.status,
            }
        }
        return { status: 'ok' }
    } catch (error) {
        console.error({ url, error })
        return {
            status: 'error',
            message: error instanceof Error ? error.message : String(error),
        }
    }
}
